import { ReactElement, useEffect, useState } from 'react';

import { Timestamp } from 'firebase/firestore';

import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import CloudUploadIcon from '@mui/icons-material/CloudUpload';

import Papa from 'papaparse';

import { useAuth } from '../../contextProviders/AuthProvider';
import GhinDaily from '../../types/GhinDaily';
import { HANDICAP_ID_CSV_HEADERS, HANDICAP_LABELS, isCsvHeader, parseGhinHandicapIndex } from '../../utils/handicapUtils';
import { readSpreadsheetFile, SPREADSHEET_FILE_ACCEPT } from '../../utils/utilsUploads';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  '&.date-column': {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  '&.selected': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
}));

type CsvGhinRow = {
  ghin: string;
  email: string;
  name: string;
  [key: string]: string; // For dynamic date columns
};

interface DailyGhinCsvUploadProps {
  onSubmit: (ghinDailies: GhinDaily[]) => void;
  onCancel: () => void;
}

// Column headers are dates in yyyy/mm/dd format; NaN sorts last if unparseable.
const parseColumnDate = (column: string): number => {
  const [year, month, day] = column.split('/').map(Number);
  return Date.UTC(year, month - 1, day);
};

export default function DailyGhinCsvUpload(props: DailyGhinCsvUploadProps): ReactElement {
  const { onSubmit, onCancel } = props;

  const { isGod } = useAuth();
  const [csvFile, setCsvFile] = useState<string>('');
  const [selectedDateColumns, setSelectedDateColumns] = useState<string[]>([]);
  const [parsedData, setParsedData] = useState<CsvGhinRow[]>([]);
  const [dateColumns, setDateColumns] = useState<string[]>([]);

  useEffect(() => {
    if (csvFile) {
      Papa.parse<CsvGhinRow>(csvFile, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (header) => (isCsvHeader(header, HANDICAP_ID_CSV_HEADERS) ? 'ghin' : header),
        complete: (results) => {
          // Extract date columns (all columns except ghin, email, name), most recent first
          const columns = Object.keys(results.data[0] || {})
            .filter((col) => !['ghin', 'email', 'name'].includes(col.toLowerCase()))
            .sort((a, b) => parseColumnDate(b) - parseColumnDate(a));
          setDateColumns(columns);
          setParsedData(results.data);
          // Auto-select the most recent date column if available
          if (columns.length > 0) {
            setSelectedDateColumns([columns[0]]);
          }
        },
      });
    }
  }, [csvFile]);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setCsvFile(await readSpreadsheetFile(file));
    }
  };

  const handleColumnClick = (column: string) => {
    setSelectedDateColumns((prev) =>
      prev.includes(column) ? prev.filter((col) => col !== column) : [...prev, column]
    );
  };

  const buildGhinDaily = (column: string): GhinDaily => {
    // Parse the date from the column header (format yyyy/mm/dd)
    const [year, month, day] = column.split('/').map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));

    const dateStr = `${year}${String(month).padStart(2, '0')}${String(day).padStart(2, '0')}`;

    return {
      date: Timestamp.fromDate(date),
      itemId: dateStr,
      indices: parsedData
        .map((row) => {
          const rawIndex = (row[column] ?? '').trim();
          if (!rawIndex || isNaN(parseGhinHandicapIndex(rawIndex))) return null;
          return {
            personId: '', // This will need to be populated based on ghin/email lookup
            ghin: row.ghin,
            index: rawIndex,
          };
        })
        .filter((index): index is NonNullable<typeof index> => index !== null)
        .sort((a, b) => a.ghin.localeCompare(b.ghin)),
    };
  };

  const handleSubmit = () => {
    if (selectedDateColumns.length === 0 || parsedData.length === 0) return;

    // Process oldest to newest, regardless of selection order
    const orderedColumns = [...selectedDateColumns].sort(
      (a, b) => parseColumnDate(a) - parseColumnDate(b)
    );

    onSubmit(orderedColumns.map(buildGhinDaily));
  };

  if (!isGod) {
    return (
      <Typography variant="bodyLarge" color="error">
        You do not have permission to access this page.
      </Typography>
    );
  }

  return (
    <Stack spacing={3}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <Button component="label" variant="contained" startIcon={<CloudUploadIcon />} disabled={!!csvFile}>
            Import CSV or XLSX file
            <VisuallyHiddenInput type="file" accept={SPREADSHEET_FILE_ACCEPT} onChange={handleFileUpload} />
          </Button>
        </Grid>

        {csvFile && parsedData.length > 0 && (
          <>
            <Grid size={{ xs: 12 }}>
              <Typography variant="headlineSmall">Preview Data</Typography>
              <Typography variant="bodyMedium" sx={{ mb: 2 }}>
                Click on one or more date column headers to select them for upload
              </Typography>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{HANDICAP_LABELS.ID}</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Name</TableCell>
                      {dateColumns.map((column) => (
                        <StyledTableCell
                          key={column}
                          className={`date-column ${selectedDateColumns.includes(column) ? 'selected' : ''}`}
                          onClick={() => handleColumnClick(column)}
                        >
                          <Checkbox
                            checked={selectedDateColumns.includes(column)}
                            size="small"
                            sx={{ p: 0, mr: 0.5, color: 'inherit' }}
                            tabIndex={-1}
                            disableRipple
                          />
                          {column}
                        </StyledTableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {parsedData.slice(0, 10).map((row, index) => (
                      <TableRow key={index}>
                        <TableCell>{row.ghin}</TableCell>
                        <TableCell>{row.email}</TableCell>
                        <TableCell>{row.name}</TableCell>
                        {dateColumns.map((column) => (
                          <TableCell key={column}>{row[column]}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {parsedData.length > 10 && (
                <Typography variant="bodyMedium" sx={{ mt: 1 }}>
                  Showing first 10 rows of {parsedData.length} total rows
                </Typography>
              )}
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Stack direction="row" spacing={2}>
                <Button variant="contained" onClick={handleSubmit} disabled={selectedDateColumns.length === 0}>
                  Submit
                </Button>
                <Button variant="outlined" onClick={onCancel}>
                  Cancel
                </Button>
              </Stack>
            </Grid>
          </>
        )}
      </Grid>
    </Stack>
  );
}
